import React from 'react';
import { Box, VStack, Flex, Text, Button } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { surveyTheme as theme } from './surveyTheme';
import { t } from './translations';

const MotionBox = motion(Box);

/**
 * IntroScreen - Welcome screen before survey begins
 *
 * Props:
 * - title: The survey title (English)
 * - titleEs: The survey title (Spanish)
 * - prologue: Introduction text (English)
 * - prologueEs: Introduction text (Spanish)
 * - questionCount: Number of questions
 * - onStart: Callback when user clicks start
 * - lang: 'en' | 'es'
 * - setLang: language setter (enables toggle)
 */
const IntroScreen = ({ title, titleEs, prologue, prologueEs, questionCount, onStart, lang = 'en', setLang }) => {
  const estimatedMinutes = Math.max(1, Math.ceil(questionCount * 0.2));
  const localized = (en, es) => (lang === 'es' && es) ? es : en;

  return (
    <Box
      minH="100vh"
      bg={theme.colors.background}
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={theme.spacing.screenPadding}
      py={12}
    >
      <MotionBox
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        width="100%"
        maxW="480px"
      >
        <VStack spacing={8} align="stretch">
          {/* Language toggle */}
          {setLang && (
            <Flex justify="flex-end">
              <Button
                size="sm"
                variant="ghost"
                fontWeight={600}
                fontFamily={theme.typography.fontFamily}
                color={theme.colors.textMuted}
                onClick={() => setLang(lang === 'en' ? 'es' : 'en')}
                _hover={{ color: theme.colors.teal }}
              >
                {t(lang, 'toggleLanguage')}
              </Button>
            </Flex>
          )}

          {/* Welcome text */}
          <VStack spacing={4} align="stretch">
            <Text
              as="h1"
              fontSize={{ base: '28px', md: '32px' }}
              fontWeight={600}
              color={theme.colors.textPrimary}
              fontFamily={theme.typography.fontFamily}
              lineHeight={1.2}
            >
              {localized(title, titleEs) || t(lang, 'welcome')}
            </Text>
            {localized(prologue, prologueEs) && (
              <Text
                fontSize={theme.typography.body.fontSize}
                lineHeight={theme.typography.body.lineHeight}
                color={theme.colors.textSecondary}
                fontFamily={theme.typography.fontFamily}
                whiteSpace="pre-wrap"
              >
                {localized(prologue, prologueEs)}
              </Text>
            )}
          </VStack>

          {/* Survey info */}
          <Box
            py={4}
            borderTop="1px solid"
            borderBottom="1px solid"
            borderColor={theme.colors.borderLight}
          >
            <Text
              fontSize="15px"
              color={theme.colors.textMuted}
              fontFamily={theme.typography.fontFamily}
            >
              {questionCount} {questionCount !== 1 ? t(lang, 'questions') : t(lang, 'question')} · {t(lang, 'aboutMinutes', { minutes: estimatedMinutes })}
            </Text>
          </Box>

          {/* Start button */}
          <Button
            onClick={onStart}
            size="lg"
            bg={theme.colors.teal}
            color="white"
            height="56px"
            fontSize="17px"
            fontWeight={600}
            fontFamily={theme.typography.fontFamily}
            borderRadius={theme.radii.button}
            _hover={{
              bg: theme.colors.tealLight,
            }}
            _active={{
              transform: 'scale(0.99)',
            }}
            transition={`background-color ${theme.transitions.normal}`}
          >
            {t(lang, 'startSurvey')}
          </Button>
        </VStack>
      </MotionBox>
    </Box>
  );
};

export default IntroScreen;
